import React from 'react';
import { useNavigate } from 'react-router-dom';

const AboutPage = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <button
        className="flex items-center gap-2 mb-6 text-primary hover:underline"
        onClick={() => navigate('/')}
      >
        <span className="material-symbols-outlined">arrow_back</span>
        Back
      </button>
      <h1 className="text-3xl font-bold mb-4">About Digital Accounting Platform</h1>
      <p className="mb-4">
        The Digital Accounting Platform was built to simplify financial management for small businesses, NGOs, freelancers, and organizations worldwide.
        Our goal is to provide a free, simple, and powerful digital tool. It helps entrepreneurs and managers create professional PDF invoices, expense reports, and project summaries using specialized, easy-to-use templates, even without advanced accounting knowledge.
        We aim to empower everyone by making professional financial tools accessible to all and helping anyone manage their operations efficiently anywhere.
      </p>
      <p className="mb-4">
        This platform aims to empower businesses in Somalia, Kenya, Ethiopia, Uganda, and neighboring countries by offering easy-to-use, professional, and localized accounting solutions.
      </p>
      <h1 className="text-3xl font-bold mb-4">
        What You Can Do: A Template for Every Need
      </h1>
      <div className="mb-4 space-y-3">
        <div>
          <span className="font-bold text-[#111418]">Grocery Sales:</span>
          <span> For grocery stores or shops. Track daily sales, list products, and calculate the total revenue on a clean sales receipt.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Kurban Organization:</span>
          <span> (Our most detailed template) For NGOs and charities. Manage donor lists (income), track expenses (slaughter, transport), write a full narrative report, and add authorized signatures to a complete activity report.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Clinic/Pharmacy:</span>
          <span> For medical clinics, dentists, or pharmacies. Create a professional invoice for a patient, listing services (consultation and lab tests) and medications.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Construction Services:</span>
          <span> For construction projects. Track all materials (cement and rebar) with their correct units (ton, m³, pcs) and log labor costs to create a detailed project expense report.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Restaurant/Cafe:</span>
          <span> For food businesses. Calculate your daily profit by tracking all sales (income) against your kitchen costs (expenses).</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Transport/Logistics:</span>
          <span> For transport companies. Calculate the profitability of a single trip by logging the freight income against all expenses (fuel, driver fees, and tolls).</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Wedding Organization:</span>
          <span> For event planners. Create a professional proposal or invoice for clients, listing all services (catering, venue, flowers), the deposit paid, and the remaining balance.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Marketing Agency:</span>
          <span> For freelancers and agencies. Bill your clients for projects by listing your services (logo design, social media management) and your fees.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Government Expense:</span>
          <span> For government departments (or corporate use). Create an official expense report for internal spending, travel, and office purchases.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Local Government:</span>
          <span> For municipal departments. Track monthly expenses by category (e.g., Infrastructure, Sanitation) and list who approved each payment.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Education Center:</span>
          <span> For private schools or training centers. Create a student payment receipt that shows tuition fees, payments made, and any remaining balances.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Auto Repair:</span>
          <span> For garages and mechanics. Generate a service invoice for customers, listing all services (labor) and parts (e.g., oil filters and brake pads) used in the repair.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">NGO/Local Organization:</span>
          <span> For simple NGO projects (such as food drives). A simple report to track donations, list expenses, and show the remaining project balance.</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Office Expense:</span>
          <span> For any business-related expenses. Track general company expenses, such as office renovations or monthly bills (rent, utilities).</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Public Supply:</span>
          <span> For public institutions (schools, hospitals). Create an inventory record or stock entry form for goods received (e.g., medicine, books).</span>
        </div>
        <div>
          <span className="font-bold text-[#111418]">Tailor/Boutique Shop:</span>
          <span> For tailors or small apparel shops. Create a customer order form that lists the items ordered and tracks the material costs (fabric and thread) for that order.</span>
        </div>
      </div>
      <h2 className="text-xl font-semibold mt-6 mb-2">Developer Information</h2>
      <p className="mb-2">Developed by: Eng. Mohamed Ibrahim Abdi (Moha-Qadar)</p>
      <p className="mb-2">Organization: Software Development East Africa (SODEAF)</p>
      <h3 className="text-lg font-semibold mt-4 mb-2">Education:</h3>
      <ul className="list-disc ml-6 mb-4">
        <li>🎓 Bachelor’s Degree – Computer Engineering, Karadeniz Technical University (Trabzon, Turkey)</li>
        <li>🎓 Master’s Degree – Computer Engineering, Fırat University (Elazığ, Turkey)</li>
        <li>🏫 High School – Sheikh Sufi Imam Hatip High School, Somalia</li>
      </ul>
      <h2 className="text-xl font-semibold mt-6 mb-2">Feedback</h2>
      <p className="mb-2">
        We’d love to hear your thoughts, suggestions, or bug reports. Feel free to reach out anytime:
      </p>
      <p className="mb-2">
        📧 Email: <a href="mailto:mohamedqadar280@gmail.com" className="text-primary underline">mohamedqadar280@gmail.com</a>
      </p>
      <p className="mb-2 flex items-center">
        <a
          href="https://wa.me/905531714783"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-primary hover:underline"
          aria-label="WhatsApp"
        >
          <span className="material-symbols-outlined text-xl mr-1" style={{ color: '#25D366' }}>chat</span>
          WhatsApp
        </a>
      </p>
      <p className="mt-4">Your feedback helps us build smarter tools for Africa’s digital future. 🌍</p>
      <div className="mt-10 text-center text-xs text-gray-500">
        © 2025 Software Development East Africa (SODEAF). All rights reserved.
      </div>
    </div>
  );
};

export default AboutPage;
